import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "../lib/api";
import { ClassAnalytics } from "../components/teacher/ClassAnalytics";
import { toast } from "sonner";
import { GraduationCap, Plus, Users, Copy, RefreshCw, Archive, ArchiveRestore, Pencil, Check, X, ChevronDown, ChevronUp } from "lucide-react";

export default function TeacherPanel() {
  const nav = useNavigate();
  const [ok, setOk] = useState(null);
  const [classes, setClasses] = useState([]);
  const [form, setForm] = useState({ name: "", description: "" });
  const [busy, setBusy] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [members, setMembers] = useState({});
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", description: "" });

  const load = async () => {
    const { data } = await api.get("/teacher/classes");
    setClasses(data);
  };

  useEffect(() => {
    (async () => {
      try {
        await api.get("/teacher/panel");
        setOk(true);
        await load();
      } catch (e) {
        const st = e?.response?.status;
        if (st === 401 || st === 403) nav("/ogretmen-basvuru", { replace: true });
        else setOk(false);
      }
    })();
    // eslint-disable-next-line
  }, [nav]);

  const createClass = async () => {
    setBusy(true);
    try {
      await api.post("/teacher/classes", form);
      toast.success("Sınıf oluşturuldu.");
      setForm({ name: "", description: "" });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sınıf oluşturulamadı");
    } finally { setBusy(false); }
  };

  const toggleOpen = async (cls) => {
    const next = openId === cls.class_id ? null : cls.class_id;
    setOpenId(next);
    setEditId(null);
    if (next && !members[next]) {
      try {
        const { data } = await api.get(`/teacher/classes/${next}/members`);
        setMembers((m) => ({ ...m, [next]: data }));
      } catch { /* sessiz */ }
    }
  };

  const copyCode = (code) => {
    navigator.clipboard?.writeText(code);
    toast.success("Katılım kodu kopyalandı.");
  };

  const regenerate = async (cls) => {
    if (!window.confirm(`"${cls.name}" için yeni katılım kodu üretilsin mi? Eski kod anında geçersiz olur.`)) return;
    try {
      await api.post(`/teacher/classes/${cls.class_id}/regenerate-code`);
      toast.success("Yeni katılım kodu üretildi.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kod yenilenemedi");
    }
  };

  const archive = async (cls) => {
    const msg = cls.is_archived
      ? `"${cls.name}" arşivden çıkarılsın mı? Katılım kodu tekrar aktif olur.`
      : `"${cls.name}" arşivlensin mi? Sınıf silinmez; yeni katılım kapanır, mevcut üyeler korunur.`;
    if (!window.confirm(msg)) return;
    try {
      await api.post(`/teacher/classes/${cls.class_id}/archive`);
      toast.success(cls.is_archived ? "Sınıf arşivden çıkarıldı." : "Sınıf arşivlendi.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "İşlem başarısız");
    }
  };

  const saveEdit = async (cls) => {
    try {
      await api.put(`/teacher/classes/${cls.class_id}`, editForm);
      toast.success("Sınıf güncellendi.");
      setEditId(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Güncellenemedi");
    }
  };

  if (!ok) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#F8FAFC]" data-testid="teacher-panel-loading">
        <div className="text-sm text-slate-500">Yükleniyor…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="teacher-panel">
      <div className="max-w-3xl mx-auto px-5 py-8 space-y-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <GraduationCap size={22} className="text-[#007BFF]" />
            <h1 className="font-display text-3xl tracking-tight text-[#0B1A35]">Öğretmen Paneli</h1>
          </div>
          <Link to="/" data-testid="teacher-panel-back" className="text-sm text-[#007BFF] hover:underline">
            ← Öğrenci görünümü
          </Link>
        </div>

        {/* Sınıf oluştur */}
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-5 space-y-3" data-testid="teacher-class-create">
          <div className="font-display text-lg">Sınıf Oluştur</div>
          <div className="grid sm:grid-cols-[1fr_1.5fr_auto] gap-2">
            <input value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Sınıf adı (ör. 9A)"
              data-testid="teacher-class-name"
              className="px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm outline-none focus:border-[#007BFF]" />
            <input value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Açıklama (isteğe bağlı)"
              data-testid="teacher-class-description"
              className="px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm outline-none focus:border-[#007BFF]" />
            <button onClick={createClass} disabled={busy || form.name.trim().length < 2}
              data-testid="teacher-class-create-btn"
              className="yz-cta inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-sm disabled:opacity-50">
              <Plus size={14} /> Oluştur
            </button>
          </div>
        </div>

        {/* Sınıf listesi */}
        <div className="space-y-2" data-testid="teacher-class-list">
          {classes.map((c) => {
            const open = openId === c.class_id;
            const editing = editId === c.class_id;
            return (
              <div key={c.class_id}
                data-testid={`teacher-class-row-${c.class_id}`}
                className={`bg-white border border-[#E2E8F0] rounded-xl overflow-hidden ${c.is_archived ? "opacity-70" : ""}`}>
                <button onClick={() => toggleOpen(c)}
                  data-testid={`teacher-class-toggle-${c.class_id}`}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-[#0B1A35] truncate">{c.name}</span>
                      {c.is_archived && (
                        <span className="px-2 py-0.5 rounded-full bg-slate-200 text-slate-600 text-[10px] font-medium"
                          data-testid={`teacher-class-archived-${c.class_id}`}>Arşivli</span>
                      )}
                    </div>
                    {c.description && <div className="text-xs text-slate-500 truncate">{c.description}</div>}
                  </div>
                  <span className="inline-flex items-center gap-1 text-xs text-slate-500"
                    data-testid={`teacher-class-membercount-${c.class_id}`}>
                    <Users size={13} /> {c.member_count ?? 0}
                  </span>
                  <span className="font-mono text-xs px-2 py-1 rounded bg-[#0B1A35] text-white tracking-widest"
                    data-testid={`teacher-class-code-${c.class_id}`}>{c.join_code}</span>
                  {open ? <ChevronUp size={14} className="text-slate-400" /> : <ChevronDown size={14} className="text-slate-400" />}
                </button>

                {open && (
                  <div className="px-4 pb-4 pt-2 border-t border-slate-100 space-y-3"
                    data-testid={`teacher-class-detail-${c.class_id}`}>
                    {editing ? (
                      <div className="grid sm:grid-cols-[1fr_1.5fr_auto] gap-2">
                        <input value={editForm.name}
                          onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                          data-testid={`teacher-class-edit-name-${c.class_id}`}
                          className="px-3 py-2 rounded-lg border border-[#007BFF] text-sm outline-none" />
                        <input value={editForm.description}
                          onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                          data-testid={`teacher-class-edit-desc-${c.class_id}`}
                          className="px-3 py-2 rounded-lg border border-[#007BFF] text-sm outline-none" />
                        <div className="flex gap-1">
                          <button onClick={() => saveEdit(c)}
                            data-testid={`teacher-class-edit-save-${c.class_id}`}
                            className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-emerald-600 text-white text-xs">
                            <Check size={12} /> Kaydet
                          </button>
                          <button onClick={() => setEditId(null)}
                            data-testid={`teacher-class-edit-cancel-${c.class_id}`}
                            className="inline-flex items-center px-3 py-2 rounded-lg border border-[#E2E8F0] text-xs">
                            <X size={12} />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-wrap items-center gap-2">
                        <button onClick={() => copyCode(c.join_code)}
                          data-testid={`teacher-class-copy-${c.class_id}`}
                          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-[#E2E8F0] text-xs hover:border-[#007BFF]">
                          <Copy size={12} /> Kodu Kopyala
                        </button>
                        <button onClick={() => regenerate(c)}
                          data-testid={`teacher-class-regen-${c.class_id}`}
                          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-[#E2E8F0] text-xs hover:border-[#007BFF]">
                          <RefreshCw size={12} /> Kodu Yenile
                        </button>
                        <button onClick={() => { setEditId(c.class_id); setEditForm({ name: c.name, description: c.description || "" }); }}
                          data-testid={`teacher-class-edit-${c.class_id}`}
                          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-[#E2E8F0] text-xs hover:border-[#007BFF]">
                          <Pencil size={12} /> Düzenle
                        </button>
                        <button onClick={() => archive(c)}
                          data-testid={`teacher-class-archive-${c.class_id}`}
                          className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs border ${
                            c.is_archived
                              ? "border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                              : "border-amber-300 text-amber-700 hover:bg-amber-50"
                          }`}>
                          {c.is_archived ? <><ArchiveRestore size={12} /> Arşivden Çıkar</> : <><Archive size={12} /> Arşivle</>}
                        </button>
                      </div>
                    )}

                    <div>
                      <div className="text-xs text-slate-500 mb-1">
                        Üyeler ({(members[c.class_id] || []).length})
                      </div>
                      {(members[c.class_id] || []).length === 0 ? (
                        <div className="text-xs text-slate-400 italic" data-testid={`teacher-class-nomembers-${c.class_id}`}>
                          Henüz üye yok — katılım kodunu öğrencilerinle paylaş.
                        </div>
                      ) : (
                        <ul className="divide-y divide-slate-100" data-testid={`teacher-class-members-${c.class_id}`}>
                          {(members[c.class_id] || []).map((m) => (
                            <li key={m.membership_id} className="py-1.5 flex items-center justify-between text-sm">
                              <span>{m.name}</span>
                              <span className="text-xs text-slate-500">{m.email} · {new Date(m.joined_at).toLocaleDateString("tr-TR")}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>

                    <ClassAnalytics classId={c.class_id} />
                  </div>
                )}
              </div>
            );
          })}
          {classes.length === 0 && (
            <div className="bg-white border border-[#E2E8F0] rounded-xl p-8 text-center text-sm text-slate-500"
              data-testid="teacher-class-empty">
              Henüz sınıfın yok. Yukarıdan ilk sınıfını oluştur.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
